import i18n from 'i18next';
import React, {useEffect, useMemo} from 'react';
import {
	ComponentUtils,
	getRouter,
	getStore,
	HeadFilterOperator,
	ListActions,
	OrganizationSelection,
	RoleSelection,
	SwList
} from 'simplifyworks-core-ui';
import {FaPlus} from 'react-icons/fa';
import {connect} from 'react-redux';
import {equalsOnlyHeadFilterOperators, FOEDUS_ROLE, likeUnaccentOnlyHeadFilterOperators} from '../../domain/constants';
import {bindActionCreators} from 'redux';
import {CountryWithFlag} from '../renderers/renderers';
import {parseSearchParams} from '../../utils/index.js';
import isEqual from 'lodash/isEqual';

const PersonList = (props) => {
	const searchParams = getRouter()?.state?.location?.search;
	const query = useMemo(() => parseSearchParams(searchParams), [searchParams]);

	const defaultColumnFilterMemoized = useMemo(() => {
		const defaultColumnFilter = {};

		if (query?.organization) {
			Object.assign(
				defaultColumnFilter,
				{
					'organization': {
						values: [{id: parseInt(query?.organization), name: query?.name}],
						operator: {...HeadFilterOperator.EQUALS, '@type': HeadFilterOperator.EQUALS.operator.toUpperCase()}
					}
				}
			);
		}

		if (query?.activeUser) {
			Object.assign(
				defaultColumnFilter,
				{
					'user.active': {
						values: [true],
						operator: {...HeadFilterOperator.EQUALS, '@type': HeadFilterOperator.EQUALS.operator.toUpperCase()}
					}
				}
			);
		}

		if (query?.usersWithNotificationsEnabled) {
			Object.assign(
				defaultColumnFilter,
				{
					'usersWithNotificationsEnabled': {
						values: [true],
						operator: {...HeadFilterOperator.EQUALS, '@type': HeadFilterOperator.EQUALS.operator.toUpperCase()}
					}
				}
			);
		}

		return defaultColumnFilter;
	}, [query]);

	useEffect(() => {
		const listFromStore = getStore()?.getState()?.lists['foedus-contacts-people-list'];

		if (listFromStore) {
			const currentOptions = listFromStore?.options;
			if (!currentOptions?.rest || isEqual(currentOptions?.columnFilters, defaultColumnFilterMemoized)) {
				return;
			}

			props?.actions?.list?.reload(
				'foedus-contacts-people-list',
				{...currentOptions, columnFilters: defaultColumnFilterMemoized}
			);
		}
	}, [defaultColumnFilterMemoized]);

	return <>
		<SwList
			name='foedus-contacts-people-list'
			restPrefix='/api/foedus/contacts/foedus-persons'
			removable={false}
			defaultColumnFilter={defaultColumnFilterMemoized}
			defaultOrdering={[{property: 'created', order: 'desc'}]}
		>
			<SwList.ActivitiesGroup>
				{props?.canManagePersons && (
					<SwList.Link
						icon={FaPlus}
						labelKey='foedus-person-create-new'
						path={'/contacts/people/new'}
					/>
				)}
			</SwList.ActivitiesGroup>

			<SwList.Table minWidth='480px'>
				<SwList.Custom
					property='person.fieldValuesMap.avatar'
					width='60px'
					label=' '
					orderable={false}
					render={resource => {
						const base64Image = resource?.person?.fieldValuesMap?.avatar;
						if (base64Image) {
							return (
								<a href={'/contacts/people/' + resource.id}>
									<img
										src={`data:image/png;base64,${base64Image}`}
										alt="Avatar"
										style={{
											maxWidth: '50px',
											maxHeight: '50px'
										}}
									/>
								</a>
							);
						} else {
							return null;
						}
					}}
				/>

				<SwList.String
					property='person.niceLabelExtended'
					labelKey='core-person-niceLabel'
					pathProperty='id'
					pathFunc={resource => '/contacts/people/' + resource.id}
					filterable
					orderable={false}
					defaultHeadFilterOperator={HeadFilterOperator.LIKE_UNACCENT}
					hiddenHeadFilterOperators={likeUnaccentOnlyHeadFilterOperators}
					width='20%'
				/>

				<SwList.Entity
					filterable
					orderable={false}
					property='organization'
					labelKey='foedus-organization'
					width='30%'
					hiddenHeadFilterOperators={equalsOnlyHeadFilterOperators}
					render={OrganizationSelection.renderText}
					pathFunc={resource => '/contacts/organizations/' + resource?.foedusOrganizationId + '/base-info'}
				>
					{OrganizationSelection.renderSelection({
						name: 'foedus-organization',
						fixedFilter: {
							organizationTypes: [props?.nationalBodyCoreCodebook]
						}
					})}
				</SwList.Entity>

				<SwList.Entity
					filterable
					orderable={false}
					property='country'
					labelKey='foedus-country'
					hiddenHeadFilterOperators={equalsOnlyHeadFilterOperators}
					renderHeadFilter={org => OrganizationSelection.renderText(org) || ''}
					render={(resource) => <CountryWithFlag
						code={resource?.code}
						name={resource?.name ?? resource?.nameEng}
					/>}
				>
					{OrganizationSelection.renderSelection({
						name: 'foedus-country',
						fixedFilter: {
							organizationTypes: [props?.countryCoreCodebook]
						}
					})}
				</SwList.Entity>

				<SwList.Boolean
					filterable
					orderable={false}
					property='user.active'
					labelKey='core-person-active'
					width='80px'
				/>

				<SwList.Entity
					filterable
					orderable={false}
					property='role'
					labelKey='core-role'
					render={resource => resource?.description}
					hiddenHeadFilterOperators={equalsOnlyHeadFilterOperators}
				>
					{RoleSelection.renderSelection({
						name: 'core-role',
						labelKey: 'core-role',
						fixedFilter: {
							roleNames: [FOEDUS_ROLE.ADMIN, FOEDUS_ROLE.COUNTRY_MANAGER, FOEDUS_ROLE.COORDINATOR]
						}
					})}
				</SwList.Entity>

				<SwList.Number
					orderable={false}
					property='registeredDevicesCount'
					labelKey='foedus-contacts-registered-devices'
					width='100px'
				/>

			</SwList.Table>

			<SwList.Info/>
			<SwList.Pagination/>

		</SwList>
	</>;
};

const mapDispatchToProps = (dispatch) => ({
	actions: {
		list: bindActionCreators(ListActions, dispatch)
	}
});

const mapStateToProps = (state) => {
	const personList = ComponentUtils.getList(state, 'foedus-contacts-people-list');
	const personListFilter = personList?.status === 'success' && personList?.options?.filterModified ? personList?.options?.filter : null;

	return {
		canManagePersons:
			state?.security?.allRoles?.includes(FOEDUS_ROLE.COUNTRY_MANAGER)
			|| state?.security?.allRoles?.includes(FOEDUS_ROLE.ADMIN),
		countryCoreCodebook: state?.foedus?.countryCoreCodebook,
		nationalBodyCoreCodebook: state?.foedus?.nationalBodyCoreCodebook,
		foedusRoles: state?.foedus?.foedusRoles,
		roleNamesFilter: state?.foedus?.foedusRoleNamesFilter,
		loggedUserParentOrganization: state?.foedus?.loggedUserParentOrganization,
		loggedUserPrimaryFoedusRole: state?.foedus?.loggedUserPrimaryFoedusRole,
		personListFilter,
		queryParams: state?.router?.location?.query

	};
};

export default {
	title: () => i18n.t('foedus-contacts-people-title-list'),
	main: connect(mapStateToProps, mapDispatchToProps)(PersonList)
};
